import './htmlEditor.scss';

import { FC, useEffect, useRef, useState } from 'react';
import { starterTemplate } from './blocks/blockLibrary';
import { BlockPalette } from './components/BlockPalette';
import { Canvas } from './components/Canvas';
import { EmptyInspector } from './components/Inspector/EmptyInspector';
import { Inspector } from './components/Inspector/Inspector';
import { LeftRail } from './components/LeftRail';
import { StyleTab } from './components/StyleTab';
import { TopToolbar } from './components/TopToolbar';
import { useBlockEditor } from './hooks/useBlockEditor';
import { useBlockHistory } from './hooks/useBlockHistory';
import { DEFAULT_GLOBAL_STYLES, type BlockType, type DeviceMode, type EditorTab, type EmailGlobalStyles, type HtmlEditorProps } from './HtmlEditor.types';
import { parseHtmlToBlocks } from './utils/parseHtmlToBlocks';
import { serializeBlocksToHtml } from './utils/serializeBlocksToHtml';

function formatSavedTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

const HtmlEditor: FC<HtmlEditorProps> = ({
  id,
  templateName = 'Untitled template',
  value = '',
  onChange,
  onTemplateNameChange,
  onClose,
  onSave,
  onPreview,
  disabled = false,
  onUploadImage,
}) => {
  const initialBlocks = value.trim() ? parseHtmlToBlocks(value) : starterTemplate();
  const { blocks, snapshot, replaceBlocks, undo, redo, canUndo, canRedo } = useBlockHistory(initialBlocks);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [device, setDevice] = useState<DeviceMode>('desktop');
  const [tab, setTab] = useState<EditorTab>('content');
  const [name, setName] = useState(templateName);
  const [savedAt, setSavedAt] = useState(() => formatSavedTime(new Date()));
  const [globalStyles, setGlobalStyles] = useState<EmailGlobalStyles>(DEFAULT_GLOBAL_STYLES);

  const dragTypeRef = useRef<BlockType | null>(null);
  const lastSyncedHtml = useRef(value);
  const skipEmitRef = useRef(true);

  const selected = blocks.find((b) => b.id === selectedId);

  const { updateBlock, deleteBlock, duplicateBlock, moveBlock, insertAt } = useBlockEditor({
    blocks,
    snapshot,
    onSelect: setSelectedId,
  });

  useEffect(() => {
    setName(templateName);
  }, [templateName]);

  useEffect(() => {
    if (value === lastSyncedHtml.current) return;
    lastSyncedHtml.current = value;
    skipEmitRef.current = true;
    const parsed = value.trim() ? parseHtmlToBlocks(value) : starterTemplate();
    replaceBlocks(parsed, true);
    setSelectedId(null);
  }, [value, replaceBlocks]);

  useEffect(() => {
    if (skipEmitRef.current) {
      skipEmitRef.current = false;
      return;
    }
    const html = serializeBlocksToHtml(blocks, globalStyles);
    if (html !== lastSyncedHtml.current) {
      lastSyncedHtml.current = html;
      onChange?.(html);
    }
  }, [blocks, globalStyles, onChange]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSavedAt(formatSavedTime(new Date()));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleNameChange = (next: string) => {
    setName(next);
    onTemplateNameChange?.(next);
  };

  const handleGlobalStylesChange = (patch: Partial<EmailGlobalStyles>) => {
    setGlobalStyles((prev) => ({ ...prev, ...patch }));
  };

  const handleInsert = (type: BlockType) => {
    insertAt(blocks.length, type);
  };

  const handlePreview = () => {
    onPreview?.(serializeBlocksToHtml(blocks, globalStyles));
  };

  const handleSave = () => {
    const html = serializeBlocksToHtml(blocks, globalStyles);
    onSave?.({ name, html });
    onClose?.();
  };

  return (
    <div id={id} className='ee-root'>
      <TopToolbar
        name={name}
        savedAt={savedAt}
        device={device}
        canUndo={canUndo}
        canRedo={canRedo}
        disabled={disabled}
        onNameChange={handleNameChange}
        onClose={onClose}
        onUndo={undo}
        onRedo={redo}
        onDeviceChange={setDevice}
        onPreview={handlePreview}
        onSave={handleSave}
      />

      <div className='ee-body'>
        <LeftRail tab={tab} onTabChange={setTab} disabled={disabled} />

        <aside className='ee-palette'>
          {tab === 'content' && <BlockPalette dragTypeRef={dragTypeRef} onInsert={handleInsert} disabled={disabled} />}
          {tab === 'style' && <StyleTab styles={globalStyles} onChange={handleGlobalStylesChange} disabled={disabled} />}
        </aside>

        <Canvas
          blocks={blocks}
          selectedId={selectedId}
          device={device}
          dragTypeRef={dragTypeRef}
          emailWidth={globalStyles.emailWidth}
          disabled={disabled}
          onSelect={setSelectedId}
          onMoveUp={(blockId) => moveBlock(blockId, -1)}
          onMoveDown={(blockId) => moveBlock(blockId, 1)}
          onDuplicate={duplicateBlock}
          onDelete={deleteBlock}
          onTextEdit={(blockId, text) => updateBlock(blockId, { text })}
          onUpdateBlock={updateBlock}
          onInsertAt={insertAt}
          onUploadImage={onUploadImage}
        />

        <aside className='ee-inspector'>
          {selected ? (
            <Inspector
              block={selected}
              disabled={disabled}
              onUploadImage={onUploadImage}
              onChange={(patch) => updateBlock(selected.id, patch)}
              onClose={() => setSelectedId(null)}
            />
          ) : (
            <EmptyInspector />
          )}
        </aside>
      </div>
    </div>
  );
};

export default HtmlEditor;
